import React, { useState, useMemo, useEffect } from "react";
import { Breadcrumb } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUserRole, loginUser } from "~/shared/auth-utils";
import ControlledForm from "~/shared/form/ControlledForm";
import TextInput from "~/shared/form/TextInput";
import SelectInput from "~/shared/form/SelectInput";
import { HOUSEHOLD_RELATIONSHIPS, PARENT_TYPES, PAYMENT_FREQUENCIES } from "./form-constants";
import { useForm, useFieldArray } from "react-hook-form";
import useAxiosData from "~/shared/use-axios-data";
import Page from "~/shared/Page";
import useNavbar from "./use-navbar";
import FormSection from "~/shared/form/FormSection";
import FieldContext from "~/shared/form/FieldContext";
import FloatingPanel from "~/shared/FloatingPanel";
import { AddressFields, EmergencyContactFields, FullNameFields } from "~/pages/family/FieldGroups";
import FormResponse from "~/shared/form/FormResponse";
import FormButton from "~/shared/form/FormButton";
import useModal from "~/shared/UseModal";
import AutoSelectInput from "~/shared/form/AutoSelectInput";
import BreadcrumbLink from "~/shared/BreadcrumbLink";
import { getFullName, getSessionData, localeFormatDate, setSessionData } from "~/shared/utils";
import DataTable from "~/shared/DataTable";
import FormAccordionSection from "~/shared/form/FormAccordionSection";
import AlertBox from "~/shared/AlertBox";

export default function FamilyLanding() {
    const { familyId } = useParams();
    const [editableSection, setEditableSection] = useState(null);
    const [lastName, setLastName] = useState(getSessionData(`family/${familyId}`)?.lastName);
    const familyData = useMemo(() => axios.post(getUserRole() === "FAMILY" ? "family/load-profile" : "admin/family/get", { id: familyId }), [familyId]);
    const [{ children, photoVideoReleaseAgreement }, status, setFamilyData] = useAxiosData(() => familyData, { parent: {}, children: [], photoVideoReleaseAgreement: {} }, [familyData], ({ parent }) => updateLastName(parent));
    const [programs] = useAxiosData(() => axios.get("programs"), []);
    const { ModalDialog, showConfirm } = useModal();

    const parentForm = useForm({
        disabled: editableSection !== "parent",
        defaultValues: async () => ({ email: (await familyData).data.email, parent: (await familyData).data.parent })
    });

    const householdForm = useForm({
        disabled: editableSection !== "household",
        defaultValues: async () => ({ household: (h => {
            h.members.filter(m => m.monthlyIncome === 0).forEach(m => m.monthlyIncome = "0");
            return h;
        })((await familyData).data.household) })
    });

    const { fields, append, remove, move } = useFieldArray({ control: householdForm.control, name: "household.members" });

    useEffect(() => {
        if (editableSection === "household") {
            setTimeout(() => {
                append();
                remove(householdForm.getValues("household.members").length - 1);
            }, 200);
        }
    }, [editableSection]);

    function noMonthlyIncome(index) {
        const monthlyIncome = householdForm.watch(`household.members.${index}.monthlyIncome`);
        return !monthlyIncome || monthlyIncome === "0";
    }

    function onSubmit(data) {
        if (getUserRole() === "ADMIN") {
            data.id = familyId;
        }

        if (data?.household?.members) {
            data.household.members = data.household.members.filter(m => !m.removed);
        }

        // Keep parent name in sync with name of first household member (the parent)
        if (data.parent) {
            data["household.members.0.firstName"] = data.parent.firstName;
            data["household.members.0.middleName"] = data.parent.middleName;
            data["household.members.0.lastName"] = data.parent.lastName;
        }
    }

    function onSuccess(response, form, data) {
        if (data.parent) {
            householdForm.resetField("household.members.0.firstName", { defaultValue: data.parent.firstName });
            householdForm.resetField("household.members.0.middleName", { defaultValue: data.parent.middleName });
            householdForm.resetField("household.members.0.lastName", { defaultValue: data.parent.lastName });
        }

        form.reset(data);
        form.reset();
        setEditableSection(null);

        if (form === parentForm) {
            updateLastName(data.parent);
        }

        if (response.token) {
            loginUser(response.token);
        }
    }

    function updateLastName(parent) {
        setLastName(parent.lastName);
        setSessionData(`family/${familyId}`, { lastName: parent.lastName });
    }

    async function deleteChild(child, context) {
        if (await showConfirm("Delete Child", `Are you sure you want to permanently delete ${child.firstName} ${child.lastName}?`)) {
            context.setLoading(true);
            await axios.post(`${getUserRole().toLowerCase()}/child/delete/${child._id}`, { id: familyId });
            setFamilyData(f => ({ ...f, children: children.filter(c => c._id !== child._id) }));
        }
    }

    async function changeChildProgram(child, programName) {
        if (!programName) {
            programName = null;
        }

        const program = programName ? programs.find(program => program.name === programName) : null;
        await axios.post(`admin/child/${child._id}`, { id: familyId, program: program?._id ?? null });
        const sessionData = getSessionData("manage-families-state");

        if (sessionData) {
            sessionData.searchResults.find(f => f._id === familyId).children.find(c => c._id === child._id).program = program;
            setSessionData("manage-families-state", sessionData);
        }
    }

    function getDisplayIndex(i) {
        let displayIndex = 0;

        for (let index = 0; index < i; index++) {
            if (!fields[index].removed) {
                displayIndex++;
            }
        }

        return displayIndex;
    }

    return (
        <Page title={getUserRole() === "FAMILY" ? "Account Information" : lastName ? `${lastName} Family` : ""} navBar={useNavbar()}>
            {ModalDialog}
            <div className="page-container">
                {getUserRole() === "ADMIN" && (
                    <Breadcrumb className="mb-3">
                        <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
                        <BreadcrumbLink href="/admin/manage-families">Manage Families</BreadcrumbLink>
                        <Breadcrumb.Item active>{lastName ? `${lastName} Family` : "..."}</Breadcrumb.Item>
                    </Breadcrumb>
                )}
                <FloatingPanel title="Children">
                    <DataTable data={children} className="mx-auto" style={{ maxWidth: "65rem" }} loading={status === "loading"}>
                        <DataTable.Column value={getFullName}>Child Name</DataTable.Column>
                        <DataTable.Column id="dateOfBirth" format={localeFormatDate}>Date of Birth</DataTable.Column>
                        {getUserRole() === "FAMILY" ? (
                            <DataTable.Column
                                id="program"
                                format={p => p ? <a href={p.url || undefined} target="_blank" rel="noopener noreferrer">{p.name}</a> : "None"}
                            >Program</DataTable.Column>
                        ) : (
                            <DataTable.Column value={child => (
                                <AutoSelectInput
                                    initial={child.program?.name} placeholder="None"
                                    update={p => changeChildProgram(child, p)} options={programs.map(p => p.name)}
                                    confirm={p => ["Change Program", `Are you sure you want to change ${child.firstName} ${child.lastName}'s program to: ${p}?`]}
                                />
                            )}>Program</DataTable.Column>
                        )}
                        <DataTable.Column value={child => <>
                            <DataTable.Action><Link to={`${getUserRole() === "FAMILY" ? "/child" : `/family/${familyId}/child`}/${child._id}`}>View</Link></DataTable.Action>
                            {getUserRole() === "ADMIN" && <DataTable.Action><Link to={`/family/${familyId}/child/${child._id}?edit`}>Edit</Link></DataTable.Action>}
                            <DataTable.Action onClick={(_, context) => deleteChild(child, context)} manualSpinner>Delete</DataTable.Action>
                        </>}>Actions</DataTable.Column>
                        <DataTable.Message condition={status === "error"}>
                            <AlertBox type="error">An unknown error occurred. Please try again later.</AlertBox>
                        </DataTable.Message>
                        <DataTable.Message condition={children.length === 0}>
                            <i className="text-muted">No children in family</i>
                        </DataTable.Message>
                    </DataTable>
                    <Link to={getUserRole() === "FAMILY" ? "/child/add" : `/family/${familyId}/child/add`} className="d-block mx-auto mt-4">
                        <FormButton formless family>Add Child</FormButton>
                    </Link>
                </FloatingPanel>

                <FloatingPanel title="Parent Information">
                    <ControlledForm hook={parentForm} onSubmit={onSubmit} post={getUserRole() === "FAMILY" ? "family/update-profile" : "admin/family/update"} onSuccess={onSuccess}>
                        <FieldContext id="parent">
                            <div className="flex flex-col gap-3">
                                <FormSection row wrap gap={1}>
                                    <FullNameFields />
                                </FormSection>
                                <FormSection row wrap gap={1}>
                                    <TextInput label="Email" absoluteId="email" preset="email" required />
                                    <TextInput label="Phone Number" id="mainPhone" preset="phone" required />
                                    <SelectInput label="Relationship" id="childRelationship" placeholder="Select relationship" options={PARENT_TYPES} required />
                                </FormSection>
                                <FormSection row wrap gap={1}>
                                    <AddressFields />
                                </FormSection>
                                <FormSection row wrap gap={1} title="Emergency Contact #1" heading>
                                    <EmergencyContactFields family />
                                </FormSection>
                                <FormSection row wrap gap={1} title="Emergency Contact #2" heading>
                                    <EmergencyContactFields family prefix="alt" />
                                </FormSection>
                            </div>
                        </FieldContext>
                        <FormResponse />
                        {editableSection === "parent" ? <div className="btn-container mt-4 justify-around">
                            <FormButton family submit>Save</FormButton>
                            <FormButton family onClick={() => (setEditableSection(null), parentForm.reset())}>Cancel</FormButton>
                        </div> : (
                            <FormButton family className="mx-auto mt-4" disabled={editableSection} onClick={() => setEditableSection("parent")}>Edit</FormButton>
                        )}
                    </ControlledForm>
                </FloatingPanel>

                <FloatingPanel title="Household Information">
                    <ControlledForm hook={householdForm} onSubmit={onSubmit} post={getUserRole() === "FAMILY" ? "family/update-profile" : "admin/family/update"} onSuccess={onSuccess}>
                        <FieldContext id="household">
                            <div className="flex flex-col gap-3">
                                <FormSection row wrap gap={1}>
                                    <TextInput label="Household Size" id="size" preset="integer" min={1} max={99} required />
                                    <TextInput label="Family Unit Size" id="familyUnitSize" preset="integer" min={1} max={99} required />
                                    <TextInput label="Household Monthly Income" id="grossMonthlyIncome" preset="money" allowMinus={false} required />
                                    <TextInput label="Household Yearly Income" id="grossYearlyIncome" preset="money" allowMinus={false} required />
                                </FormSection>
                                <FormAccordionSection title="Household Members" id="household.members" bodyClassName="d-flex flex-column gap-3">
                                    {fields.map((field, index) => !field.removed && <div key={field.id} className="border rounded shadow-sm p-3">
                                        <h3 className="text-xl">Household Member #{getDisplayIndex(index) + 1}</h3>
                                        <FieldContext id={`members.${index}`}>
                                            <div className="flex flex-col gap-3">
                                                <FormSection row wrap gap={1}>
                                                    <FullNameFields frozen={index === 0} />
                                                </FormSection>
                                                <FormSection row wrap gap={1}>
                                                    <SelectInput label="Relationship" id="relationship" minWidth={8} placeholder="Select relationship" options={index === 0 ? ["Self"] : HOUSEHOLD_RELATIONSHIPS} frozen={index === 0} required />
                                                    <TextInput label="Date of Birth" id="dateOfBirth" preset="birthDate" required />
                                                </FormSection>
                                                <FormSection row wrap gap={1}>
                                                    <TextInput label="Monthly Income" id="monthlyIncome" preset="money" allowMinus={false} deps={[`household.members.${index}.incomeSource`, `household.members.${index}.oftenReceived`]} required>
                                                        <span className="text-neutral-600 text-sm text-left mt-1">Enter income before taxes and deductions. Enter $0 for no income.</span>
                                                    </TextInput>
                                                    <TextInput label="Income Source" id="incomeSource" disabled={noMonthlyIncome(index)} required />
                                                    <SelectInput label="How Often Received" id="oftenReceived" disabled={noMonthlyIncome(index)} options={PAYMENT_FREQUENCIES} placeholder="Select frequency" required />
                                                </FormSection>
                                            </div>
                                        </FieldContext>
                                        {index > 0 && editableSection === "household" && <FormButton formless className="mx-auto text-lg mt-3" type="button" onClick={() => (householdForm.setValue(`household.members.${index}.removed`, true), move(0, 0))}>Remove</FormButton>}
                                    </div>)}
                                    {editableSection === "household" && <FormButton formless family className="mx-auto" type="button" onClick={() => append({ firstName: "", middleName: "", lastName: "", monthlyIncome: "" })}>Add Member</FormButton>}
                                </FormAccordionSection>
                            </div>
                        </FieldContext>
                        <FormResponse />
                        {editableSection === "household" ? <div className="btn-container justify-around">
                            <FormButton family submit>Save</FormButton>
                            <FormButton family onClick={() => (setEditableSection(null), householdForm.reset())}>Cancel</FormButton>
                        </div> : (
                            <FormButton family className="mx-auto" disabled={editableSection} onClick={() => setEditableSection("household")}>Edit</FormButton>
                        )}
                    </ControlledForm>
                </FloatingPanel>

                <FloatingPanel title="Photo/Video Release Form">
                    <p className="">{photoVideoReleaseAgreement.signed ? `Signed on ${new Date(photoVideoReleaseAgreement.date).toLocaleDateString()}` : "Not signed"}</p>
                </FloatingPanel>
            </div>
        </Page >
    );
}
